/**
 * ClickReel SDK - Overlay Choices Component
 *
 * Multiple-choice question overlay that slides up from the bottom of the video.
 * Follows the ProductCard render/show/hide/destroy lifecycle pattern.
 * Used in interactive campaigns for branching video flows.
 */

import { OverlayChoicesConfig, InteractionEdge } from './types';

export interface OverlayChoicesOptions {
  config: OverlayChoicesConfig;
  edges: InteractionEdge[];
  primaryColor: string;
  onChoiceSelected: (edgeId: string, choiceLabel: string) => void;
}

export class OverlayChoices {
  private element: HTMLElement | null = null;
  private shown = false;
  private options: OverlayChoicesOptions;

  constructor(options: OverlayChoicesOptions) {
    this.options = options;
  }

  render(container: HTMLElement): void {
    this.element = document.createElement('div');
    this.element.className = 'clickreel-overlay-choices';
    this.element.setAttribute('role', 'group');
    this.element.setAttribute('aria-label', 'Choose an option');

    const inner = document.createElement('div');
    inner.className = 'clickreel-overlay-choices-inner';

    // Question text (optional)
    if (this.options.config.questionText) {
      const question = document.createElement('div');
      question.className = 'clickreel-overlay-choices-question';
      question.textContent = this.options.config.questionText;
      inner.appendChild(question);
    }

    // Choice buttons — match edges to choices by sort order
    const choices = this.options.config.choices || [];
    const edges = this.options.edges || [];

    // Sprint 005 (2026-04-29): warn once when the campaign is misconfigured
    // (more choices than edges). The mismatched buttons render in a disabled
    // state so users see them as visibly inert rather than silently dead.
    if (choices.length > edges.length) {
      console.warn(
        `[ClickReel] OverlayChoices: ${choices.length} choices defined but only ${edges.length} edges. ` +
          `Trailing choices will be rendered disabled. Check the campaign flow editor for missing edges.`,
      );
    }

    choices.forEach((choice, index) => {
      const edge = edges[index];
      const isDisabled = !edge;
      const btn = document.createElement('button');
      btn.className = 'clickreel-overlay-choices-btn';
      btn.setAttribute('role', 'button');
      btn.setAttribute('aria-label', `Choice ${choice.label}: ${choice.text}`);

      // Letter label
      const label = document.createElement('span');
      label.className = 'clickreel-overlay-choices-label';
      label.textContent = choice.label || String.fromCharCode(65 + index); // A, B, C...
      label.style.background = this.options.primaryColor;
      btn.appendChild(label);

      // Choice text
      const text = document.createElement('span');
      text.className = 'clickreel-overlay-choices-text';
      text.textContent = choice.text;
      btn.appendChild(text);

      if (isDisabled) {
        // Sprint 005 (2026-04-29): visibly inert state for choices with no
        // matching edge. Pre-fix, these rendered as fully-styled buttons that
        // silently no-op'd on click — users saw them as broken UI.
        btn.disabled = true;
        btn.setAttribute('aria-disabled', 'true');
        btn.style.opacity = '0.45';
        btn.style.cursor = 'not-allowed';
        btn.style.pointerEvents = 'none';
      } else {
        // Hover effect
        btn.addEventListener('mouseenter', () => {
          btn.style.borderColor = this.options.primaryColor;
        });
        btn.addEventListener('mouseleave', () => {
          btn.style.borderColor = 'rgba(255, 255, 255, 0.2)';
        });

        // Click handler
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          this.options.onChoiceSelected(edge.id, edge.choiceLabel);
        });
      }

      inner.appendChild(btn);
    });

    this.element.appendChild(inner);

    // Inject styles
    this.injectStyles(container);

    container.appendChild(this.element);
  }

  show(): void {
    if (this.shown || !this.element) return;
    this.shown = true;
    requestAnimationFrame(() => {
      this.element?.classList.add('visible');
    });
  }

  hide(): void {
    if (!this.element) return;
    this.element.classList.remove('visible');
  }

  destroy(): void {
    this.element?.remove();
    this.element = null;
    this.shown = false;
  }

  get triggerPercent(): number {
    return this.options.config.triggerAtPercent ?? 90;
  }

  get triggerOnComplete(): boolean {
    return this.options.config.triggerOnComplete ?? false;
  }

  get hasShown(): boolean {
    return this.shown;
  }

  private injectStyles(container: HTMLElement): void {
    const styleId = 'clickreel-overlay-choices-styles';
    if (container.querySelector(`#${styleId}`)) return;

    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      .clickreel-overlay-choices {
        position: relative;
        bottom: 0;
        left: 0;
        right: 0;
        z-index: 100;
        pointer-events: auto;
        transform: translateY(100%);
        opacity: 0;
        transition: transform 0.4s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.3s ease;
      }
      .clickreel-overlay-choices.visible {
        transform: translateY(0);
        opacity: 1;
      }
      .clickreel-overlay-choices-inner {
        padding: 16px;
        background: linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.7) 60%, transparent 100%);
        display: flex;
        flex-direction: column;
        gap: 8px;
      }
      .clickreel-overlay-choices-question {
        color: #fff;
        font-size: 15px;
        font-weight: 600;
        text-align: center;
        margin-bottom: 4px;
        text-shadow: 0 1px 3px rgba(0,0,0,0.5);
      }
      .clickreel-overlay-choices-btn {
        display: flex;
        align-items: center;
        gap: 10px;
        width: 100%;
        padding: 10px 14px;
        background: rgba(255, 255, 255, 0.1);
        backdrop-filter: blur(8px);
        -webkit-backdrop-filter: blur(8px);
        border: 1px solid rgba(255, 255, 255, 0.2);
        border-radius: 10px;
        color: #fff;
        font-size: 14px;
        cursor: pointer;
        transition: background 0.2s ease, border-color 0.2s ease, transform 0.15s ease;
        text-align: left;
        -webkit-tap-highlight-color: transparent;
      }
      .clickreel-overlay-choices-btn:hover {
        background: rgba(255, 255, 255, 0.18);
      }
      .clickreel-overlay-choices-btn:active {
        transform: scale(0.97);
      }
      .clickreel-overlay-choices-label {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 28px;
        height: 28px;
        min-width: 28px;
        border-radius: 8px;
        color: #fff;
        font-size: 13px;
        font-weight: 700;
      }
      .clickreel-overlay-choices-text {
        flex: 1;
        line-height: 1.3;
      }
    `;
    container.appendChild(style);
  }
}
